/**
 * Same-origin API client. All requests prefix `/api` and include cookies.
 * The frontend is served from the same Express process as the API in
 * production, and in dev Vite proxies /api -> http://localhost:3001.
 */

const API_BASE = "/api";

export class ApiClientError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

/** Shared request: applies credentials/headers, the 401 PENDING_APPROVAL
 *  redirect, and ApiClientError shaping; returns the parsed JSON envelope. */
async function request(path: string, init?: RequestInit): Promise<any> {
  const url = path.startsWith("/") ? `${API_BASE}${path}` : `${API_BASE}/${path}`;
  const res = await fetch(url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
    ...init,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({
      error: { code: "UNKNOWN", message: "Request failed" },
    }));

    if (res.status === 401 && body.error?.code === "PENDING_APPROVAL") {
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/pending-approval")) {
        window.location.replace("/pending-approval");
      }
    }

    throw new ApiClientError(
      body.error?.code ?? "UNKNOWN",
      body.error?.message ?? "Request failed",
      res.status,
    );
  }

  return res.json();
}

/** Fetch and unwrap the `{ data }` envelope (the common case). */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const json = await request(path, init);
  return json.data as T;
}

/** Fetch the FULL response envelope — use when the endpoint returns sibling
 *  fields alongside `data` (e.g. `nextCursor`) that `apiFetch` would drop. */
export async function apiFetchEnvelope<T>(path: string, init?: RequestInit): Promise<T> {
  return (await request(path, init)) as T;
}
